import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
// import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import EventBus from '../../../../../utilities/event-bus';
import { showToast } from '@/utilities/toast-handler';
import { formatTreeDatatable, formatTreeData, formatVueTreeSelectData, flattenArray } from '../../../../../utilities/json-formatter';
import { useAuthStore } from '@/modules/auth/store';
import { useCompanyStore } from '@/modules/company/store';

const DEPARTMENT_URL = process.env.VUE_APP_API_VERSION + '/admin/departments';
const USER_DEPARTMENT_URL = process.env.VUE_APP_API_VERSION + '/admin/users';

export const useDepartmentStore = defineStore('department', () => {
    const authStore = useAuthStore();
    const companyStore = useCompanyStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId || companyStore.activeCompany?.id;
    });

    //state
    const department = ref({
        name: null,
        parent_id: null,
        company_id: null,
    });

    const departments = ref([]);
    const loading = ref(false);
    const editId = ref(null);
    const departmentUsers = ref([]);
    const loggedUserDepartments = ref([]);

    // getters
    const departmentTreeTable = computed(() => {
        return formatTreeDatatable(departments.value);
    });

    const departmentTree = computed(() => {
        return formatTreeData(departments.value);
    });

    const departmentVueTree = computed(() => {
        return formatVueTreeSelectData(departments.value);
    });

    const flatDepartments = computed(() => {
        return flattenArray(departments.value);
    });

    const loggedUserDepartmentTree = computed(() => {
        return formatTreeData(departments.value);
    });

    const loggedUserDepartmentVueTree = computed(() => {
        return formatVueTreeSelectData(loggedUserDepartments.value);
    });

    // getters
    const deptUsers = computed(() => {
        if (!Array.isArray(departmentUsers.value) || departmentUsers.value.length === 0) return []
      
        // 1 Count how many times each fullname appears
        const nameCount = {}
        for (const user of departmentUsers.value) {
          if (!user.name || !user.surname) continue
          const fullname = `${user.name} ${user.surname}`.trim()
          nameCount[fullname] = (nameCount[fullname] || 0) + 1
        }
      
        // 2 Create a Set of duplicate fullnames
        const duplicateNameSet = new Set(
          Object.keys(nameCount).filter((name) => nameCount[name] > 1)
        )
      
        // 3 Map final list (with ID appended only for duplicates)
        const uniqueUsersMap = new Map()
      
        for (const user of departmentUsers.value) {
          if (!user.id) continue // skip missing ID
      
          if (!uniqueUsersMap.has(user.id)) {
            const baseName =
              user.name && user.surname
                ? `${user.name.charAt(0).toUpperCase() + user.name.slice(1)} ${user.surname.charAt(0).toUpperCase() + user.surname.slice(1)}`
                : user.email || 'Unknown User'
      
            // Append ID only if name is duplicated
            const fullname = duplicateNameSet.has(baseName)
              ? `${baseName} (${user.id})`
              : baseName
      
            uniqueUsersMap.set(user.id, {
              ...user,
              fullname,
            })
          }
        }
      
        return Array.from(uniqueUsersMap.values())
      })
 

    //actions
    const fetchAllDepartments = async () => {
        try {
            // NProgress.start();
            loading.value = true;
            const response = await authApi.get(DEPARTMENT_URL, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                departments.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch departments');
        } finally {
            // NProgress.done();
            loading.value = false;
        }
    };

    const createDepartment = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...department.value, company_id: company_id.value };
            const response = await authApi.post(DEPARTMENT_URL, bodyParams);
            if (response.statusCode === 201) {
                fetchAllDepartments();
                showToast(response.toast, 'New department added successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to add department');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const updateDepartment = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...department.value, company_id: company_id.value };
            const response = await authApi.patch(`${DEPARTMENT_URL}/${editId.value}`, bodyParams);
            if (response.statusCode === 200) {
                fetchAllDepartments();
                showToast(response.toast, 'Department updated successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update department');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const deleteDepartment = async (departmentId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.delete(`${DEPARTMENT_URL}/${departmentId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                fetchAllDepartments();
                showToast(response.toast, 'Department deleted successfully');
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete department');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const fetchDepartmentDetails = async (departmentId) => {
        try {
            const response = await authApi.get(`${DEPARTMENT_URL}/${departmentId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch department details');
        }
    };

    const getDepartmentUsers = async (departmentId, search = null) => {
        try {
            loading.value = true;
            EventBus.emit('show-operation-loader');
            
            const params = {
                companyId: company_id.value,
            };

            // Add search parameter if provided
            if (search) {
                params.search = search;
            }

            const response = await authApi.get(`${DEPARTMENT_URL}/${departmentId}/users`, {
                params: params,
            });
            if (response.statusCode === 200) {
                departmentUsers.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch department details');
        } finally {
            loading.value = false;
            EventBus.emit('hide-operation-loader');
        }
    };

    const appointManager = async (departmentId, userId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.post(`${DEPARTMENT_URL}/${departmentId}/users/${userId}/set`, {}, {
                params: {
                    companyId: company_id.value,
                },
            });
            if (response.statusCode === 200 || response.statusCode === 201) {
                fetchAllDepartments();
                showToast(response.toast, 'User appointed as department manager');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to appoint manager');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const removeManager = async (departmentId, userId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.post(`${DEPARTMENT_URL}/${departmentId}/users/${userId}/remove`, {}, {
                params: {
                    companyId: company_id.value,
                },
            });
            if (response.statusCode === 200 || response.statusCode === 201) {
                fetchAllDepartments();
                showToast(response.toast, 'User removed as department manager');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to remove manager');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const fetchLoggedUserDepartments = async (userId) => {
        loggedUserDepartments.value = await fetchUserDepartments(userId);
    };

    const fetchUserDepartments = async (userId) => {
        try {
            const response = await authApi.get(`${USER_DEPARTMENT_URL}/${userId}/department`, {
                params: {
                    companyId: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch logged user department details');
        }
    };

    // helpers
    const initdepartment = () => {
        department.value.name = null;
        department.value.parent_id = null;
        department.value.company_id = null;
        editId.value = null;
    };

    const createEditDepartmentBody = (editDepartment) => {
        editId.value = editDepartment?.key;
        department.value.name = editDepartment?.data?.name;
        department.value.parent_id = editDepartment?.data?.parent_id;
    };
    return {
        //state
        department,
        departments,
        editId,
        loggedUserDepartments,

        //getters
        departmentTreeTable,
        departmentTree,
        departmentVueTree,
        deptUsers,
        flatDepartments,
        loggedUserDepartmentTree,
        loggedUserDepartmentVueTree,

        //actions
        fetchAllDepartments,
        createDepartment,
        updateDepartment,
        deleteDepartment,
        fetchDepartmentDetails,
        getDepartmentUsers,
        appointManager,
        removeManager,
        fetchLoggedUserDepartments,
        fetchUserDepartments,

        //helpers
        initdepartment,
        createEditDepartmentBody,
    };
});
